import { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as HELPER from "../config/helper";
import { STATUS_CODES } from "../config/constant";
import InsuranceService from "../services/InsuranceService";
import FullPageLoader from "./FullPageLoader";
import Modal from "./Modal";

interface AdditionalDiscountPopupProps {
  show: boolean;
  leadId: string | number;
  existingDiscountPercent: number;
  premiumAmount: number;
  transactionFee: number;
  adminFee: number;
  agentCommissionPercent: number;
  handleClose: () => void;
  handleChoosePaymentMethod: (discountData: AdditionalDiscountData) => void;
}

// GACOM-9867: one absolute Customer Discount replaces Initial / Additional / Total Discount
export interface AdditionalDiscountData {
  customerDiscountPercent: number;
  customerDiscountAmount: number;
  customerDiscountType: number;
  offsetPointsPercent: number;
  offsetPointsTaxPercent: number;
  netOffsetPointsPercent: number;
  effectiveOffsetPointsAmount: number;
  agentCommissionPercent: number;
  pointCommissionPercent: number;
  offsetPointsAmount: number;
  offsetPointsType: number;
  gwpAmount: number;
  agentCommissionAmount: number;
  agentPointAmount: number;
  premiumAmountPayable: number;
  netCommissionEarned: number;
}

interface InsurerQuotation {
  insurer_id?: string | number;
  customer_discount_type?: string | number;
  customer_discount_per?: string | number;
  customer_discount?: string | number;
  offset_point?: string | number;
  offset_points_amount?: string | number;
  effective_offset_points_amount?: string | number;
  agent_commision?: string | number;
  point_commision?: string | number;
  max_point_commision?: string | number;
  agent_comission_amount?: string | number;
  agent_point_amount?: string | number;
  gwp_amount?: string | number;
  final_premium?: string | number;
}

// Percentages are shown with up to 4 decimals and no trailing zeros (12.5 not 12.5000)
const formatPercent = (value: number) => {
  return `${HELPER.roundPercent(value)}%`;
};

// Percentage comparisons use the UI input precision (4 dp); amounts keep roundAmount
const roundPercent = (value: number) => HELPER.roundPercent(value);
// % limits are compared on raw values; EPS absorbs float noise, STEP is the smallest typeable % (4 dp)
const PERCENT_EPS = 1e-9;
const PERCENT_STEP = 0.0001;
// round DOWN to the 4-dp input precision (for "max you may type" limits)
const floorPercent = (value: number) => Math.floor(value * 10000 + 1e-6) / 10000;

// Server-computed % (e.g. derived from an amount) can carry up to 6 decimals;
// show it at the 4-dp input precision so it never trips the "max 4 decimals" check.
const toPercentInput = (value: unknown) => String(HELPER.roundPercent(Number(value ?? 0)));

const roundAmount = (value: number) => {
  return Math.round(value * 100) / 100;
};

const removeLeadingZero = (value: string) => {
  return value.replace(/^0+(?=\d)/, "");
};

const AdditionalDiscountPopup = ({
  show,
  leadId,
  handleClose,
  handleChoosePaymentMethod,
}: AdditionalDiscountPopupProps) => {
  const [customerDiscount, setCustomerDiscount] = useState("0");
  const [offsetPoints, setOffsetPoints] = useState("0");
  const [customerDiscountAmountInput, setCustomerDiscountAmountInput] =
    useState("0");
  const [offsetPointsAmountInput, setOffsetPointsAmountInput] = useState("0");
  const [effectiveOffsetPointsAmountInput, setEffectiveOffsetPointsAmountInput] =
    useState("0");
  const [insurerQuotation, setInsurerQuotation] =
    useState<InsurerQuotation | null>(null);
  const [maxDiscountPercent, setMaxDiscountPercent] = useState(0);
  const [maxDiscountAmount, setMaxDiscountAmount] = useState(0);
  const [offsetPointsTaxPercent, setOffsetPointsTaxPercent] = useState(0);
  const [offsetPointsTaxAmount, setOffsetPointsTaxAmount] = useState(0);
  const [isFetchingQuotation, setIsFetchingQuotation] = useState(false);
  const [isQuotationPending, setIsQuotationPending] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState("");
  const [isPopupInitialized, setIsPopupInitialized] = useState(false);
  // 1 = the % input is the source, 2 = the amount input is the source
  const [customerDiscountType, setCustomerDiscountType] = useState<1 | 2>(1);
  // 1 = %, 2 = amount, 3 = effective (after-tax) amount
  const [offsetPointsType, setOffsetPointsType] = useState<1 | 2 | 3>(1);
  const hasRequestedInitialQuotation = useRef(false);
  const hasUserEditedInputs = useRef(false);
  const insurerQuotationRequestId = useRef(0);

  const customerDiscountValue = Number(customerDiscount || 0);
  const offsetPointsValue = Number(offsetPoints || 0);
  const customerDiscountAmountValue = Number(customerDiscountAmountInput || 0);
  const offsetPointsAmountInputValue = Number(offsetPointsAmountInput || 0);
  // In amount mode the % input shows the server value rounded to 4 dp; use the exact server value for limits.
  const customerDiscountPercentExact =
    customerDiscountType === 1
      ? customerDiscountValue
      : Number(insurerQuotation?.customer_discount_per ?? customerDiscountValue);
  const gwpAmount = Number(insurerQuotation?.gwp_amount ?? 0);
  const netAgentCommissionPercent = Number(
    insurerQuotation?.agent_commision ?? 0
  );
  const displayedAgentPointsPercent = Number(
    insurerQuotation?.point_commision ?? 0
  );
  const maximumUsablePointsPercent = Number(
    insurerQuotation?.max_point_commision ?? 0
  );
  const agentCommissionAmount = Number(
    insurerQuotation?.agent_comission_amount ?? 0
  );
  const agentPointAmount = Number(
    insurerQuotation?.agent_point_amount ?? 0
  );
  const premiumAmountPayable = Number(
    insurerQuotation?.final_premium ?? 0
  );
  const offsetPointsAmount = Number(
    insurerQuotation?.offset_points_amount ?? 0
  );
  const netCommissionEarned = agentCommissionAmount;
  const netPointsEarned = agentPointAmount;
  const isCustomerDiscountAmountAtMax =
    customerDiscountType === 2 &&
    maxDiscountAmount > 0 &&
    roundAmount(customerDiscountAmountValue) === roundAmount(maxDiscountAmount);
  // Customer Cashback unlocks only when Customer Discount (%) is at the max (25%),
  // within the 4-dp input step (an amount entry may resolve to e.g. 24.99998%)
  const isOffsetPointsEditable =
    (maxDiscountPercent > 0 &&
      maxDiscountPercent - customerDiscountPercentExact < PERCENT_STEP - PERCENT_EPS &&
      customerDiscountPercentExact <= maxDiscountPercent + PERCENT_EPS) ||
    isCustomerDiscountAmountAtMax;
  // In amount modes the input shows the server % rounded to 4 dp; calculations use the server's full 6-dp value.
  const offsetPointsPercentExact =
    offsetPointsType === 1
      ? offsetPointsValue
      : Number(insurerQuotation?.offset_point ?? offsetPointsValue);
  const netOffsetPointsPercent = isOffsetPointsEditable
    // sent to the payment API: keep the 6-dp backend precision (only strip float noise)
    ? +(offsetPointsPercentExact - (offsetPointsPercentExact * offsetPointsTaxPercent) / 100).toFixed(6)
    : 0;
  const effectiveMaximumOffsetPercent = roundPercent(
    maximumUsablePointsPercent -
      (maximumUsablePointsPercent * offsetPointsTaxPercent) / 100
  );
  const effectiveOffsetPointsAmountInputValue = Number(
    effectiveOffsetPointsAmountInput || 0
  );
  // whole Rupiah: a 2-dp gross (max effective / (1 - tax) = x.41) would exceed the whole-Rupiah max and block the max entry
  const computedGrossOffsetPointsAmountFromEffective =
    offsetPointsTaxPercent < 100
      ? Math.round(
          effectiveOffsetPointsAmountInputValue / (1 - offsetPointsTaxPercent / 100)
        )
      : 0;
  const currentGrossOffsetPointsAmount =
    offsetPointsType === 2
      ? offsetPointsAmountInputValue
      : offsetPointsType === 3
      ? computedGrossOffsetPointsAmountFromEffective
      : offsetPointsAmountInputValue;
  const displayedEffectiveOffsetPointsAmount = isOffsetPointsEditable
    ? roundAmount(
        currentGrossOffsetPointsAmount -
          (currentGrossOffsetPointsAmount * offsetPointsTaxPercent) / 100
      )
    : 0;
  const maxEffectiveOffsetPointsAmount = roundAmount(
    offsetPointsTaxAmount - (offsetPointsTaxAmount * offsetPointsTaxPercent) / 100
  );
  const appliedOffsetPointsForRequest = isOffsetPointsEditable
    ? offsetPointsValue
    : 0;
  const requestCustomerDiscountPercent =
    customerDiscountType === 1 ? customerDiscountValue : 0;
  const requestCustomerDiscountAmount =
    customerDiscountType === 2 ? customerDiscountAmountValue : 0;
  const requestOffsetPointsPercent =
    offsetPointsType === 1 ? appliedOffsetPointsForRequest : 0;
  const requestOffsetPointsAmount = !isOffsetPointsEditable
    ? 0
    : offsetPointsType === 2
    ? offsetPointsAmountInputValue
    : offsetPointsType === 3
    ? computedGrossOffsetPointsAmountFromEffective
    : 0;
  // 0..max; the discount is also capped by the agent commission still available
  const customerDiscountTypingLimit = Math.max(
    Math.min(
      maxDiscountPercent,
      floorPercent(netAgentCommissionPercent + customerDiscountValue)
    ),
    0
  );
  const offsetPointsTypingLimit = Math.min(
    maximumUsablePointsPercent,
    displayedAgentPointsPercent + offsetPointsValue
  );
  // max 4 decimals may be entered for the % inputs
  // (only checked when the user is entering the % — amount modes derive it server-side)
  const isDiscountPrecisionValid =
    customerDiscountType !== 1 || HELPER.isValidDecimal(customerDiscount);
  const isOffsetPointsPrecisionValid =
    offsetPointsType !== 1 || HELPER.isValidDecimal(offsetPoints);
  const isValidDiscount =
    isDiscountPrecisionValid &&
    customerDiscountValue >= 0 &&
    (customerDiscountType !== 1 ||
      customerDiscountValue <= customerDiscountTypingLimit + PERCENT_EPS) &&
    customerDiscountPercentExact <= maxDiscountPercent + PERCENT_EPS;
  const isValidOffsetPoints =
    !isOffsetPointsEditable ||
    (isOffsetPointsPrecisionValid &&
      offsetPointsValue >= 0 &&
      (offsetPointsType !== 1 || offsetPointsValue <= offsetPointsTypingLimit + PERCENT_EPS));
  const isValidCustomerDiscountAmount =
    customerDiscountAmountValue >= 0 &&
    roundAmount(customerDiscountAmountValue) <= roundAmount(maxDiscountAmount);
  // each cashback input is only validated while it is the one being used, so a stale value from another field can't block the quotation
  const isValidOffsetPointsAmount =
    !isOffsetPointsEditable ||
    offsetPointsType !== 2 ||
    (offsetPointsAmountInputValue >= 0 &&
      roundAmount(offsetPointsAmountInputValue) <= roundAmount(offsetPointsTaxAmount));
  const isValidEffectiveOffsetPointsAmount =
    !isOffsetPointsEditable ||
    offsetPointsType !== 3 ||
    (effectiveOffsetPointsAmountInputValue >= 0 &&
      roundAmount(computedGrossOffsetPointsAmountFromEffective) <=
        roundAmount(offsetPointsTaxAmount));
  const shouldShowValidationErrors =
    !isFetchingQuotation && insurerQuotation !== null;
  const areInputsValid =
    isValidDiscount &&
    isValidOffsetPoints &&
    isValidCustomerDiscountAmount &&
    isValidOffsetPointsAmount &&
    isValidEffectiveOffsetPointsAmount;

  useLayoutEffect(() => {
    if (!show) {
      setIsPopupInitialized(false);
      return;
    }

    setInsurerQuotation(null);
    setMaxDiscountPercent(0);
    setMaxDiscountAmount(0);
    setOffsetPointsTaxPercent(0);
    setOffsetPointsTaxAmount(0);
    setIsFetchingQuotation(false);
    setIsQuotationPending(false);
    setApiErrorMessage("");
    setCustomerDiscount("0");
    setOffsetPoints("0");
    setCustomerDiscountAmountInput("0");
    setOffsetPointsAmountInput("0");
    setEffectiveOffsetPointsAmountInput("0");
    setCustomerDiscountType(1);
    setOffsetPointsType(1);
    hasRequestedInitialQuotation.current = false;
    hasUserEditedInputs.current = false;
    insurerQuotationRequestId.current += 1;
    setIsPopupInitialized(true);
  }, [show]);

  useEffect(() => {
    // cashback is only allowed at the max discount: clear it once the discount drops below
    // (skipped until the first quote has loaded, so prefilled cashback is not wiped)
    if (!isOffsetPointsEditable && insurerQuotation !== null) {
      setOffsetPoints("0");
      setOffsetPointsAmountInput("0");
      setEffectiveOffsetPointsAmountInput("0");
      setOffsetPointsType(1);
    }
  }, [isOffsetPointsEditable]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      !show ||
      !leadId ||
      !isPopupInitialized
    ) return;

    // prefill = 1st request on open: the API quotes with the values last saved for the lead
    const requestInsurerQuotation = (prefill = false) => {
      const requestId = ++insurerQuotationRequestId.current;
      setIsFetchingQuotation(true);
      const payload = prefill
        ? { lead_id: Number(leadId), customer_discount_type: 0 }
        : {
            lead_id: Number(leadId),
            customer_discount_type: customerDiscountType,
            customer_discount_per: requestCustomerDiscountPercent,
            customer_discount: requestCustomerDiscountAmount,
            offset_points: requestOffsetPointsPercent,
            offset_points_amount: requestOffsetPointsAmount,
          };

      InsuranceService.insurerListByLeadId(payload)
        .then((response: any) => {
          if (requestId !== insurerQuotationRequestId.current) return;

          if (
            response?.data?.status &&
            Number(response.data.status) !== STATUS_CODES["success"]
          ) {
            setApiErrorMessage(
              response?.data?.message || "Unable to fetch insurer quotation."
            );
            setInsurerQuotation(null);
            setIsFetchingQuotation(false);
            setIsQuotationPending(false);
            return;
          }

          setApiErrorMessage("");
          const responseData = response?.data?.data || {};
          const quotations: InsurerQuotation[] = responseData.quotation || [];
          const quotation = quotations[0];

          setMaxDiscountPercent(Number(responseData.max_discount_per ?? 0));
          setMaxDiscountAmount(Number(responseData.max_discount_amount ?? 0));
          setOffsetPointsTaxPercent(
            Number(responseData.offset_points_tax_percent ?? 0)
          );
          setOffsetPointsTaxAmount(
            Number(responseData.offset_points_tax_amount ?? 0)
          );

          if (prefill && quotation) {
            const savedDiscountPercent = Number(quotation.customer_discount_per ?? 0);
            setCustomerDiscount(toPercentInput(savedDiscountPercent));
            setCustomerDiscountAmountInput(
              String(Number(quotation.customer_discount ?? 0))
            );
            // A saved amount entry (or a % with more than 4 decimals) keeps the amount as the
            // source of truth so resubmitting doesn't re-round it.
            if (
              Number(quotation.customer_discount_type) === 2 ||
              !HELPER.isValidDecimal(savedDiscountPercent)
            ) {
              setCustomerDiscountType(2);
            }
            setOffsetPoints(toPercentInput(quotation.offset_point));
            if (!HELPER.isValidDecimal(Number(quotation.offset_point ?? 0))) {
              setOffsetPointsType(2);
            }
            setOffsetPointsAmountInput(
              String(Number(quotation.offset_points_amount ?? 0))
            );
            setEffectiveOffsetPointsAmountInput(
              String(Number(quotation.effective_offset_points_amount ?? 0))
            );
          } else if (quotation) {
            if (customerDiscountType === 1) {
              setCustomerDiscountAmountInput(
                String(Number(quotation.customer_discount ?? 0))
              );
            } else {
              setCustomerDiscount(toPercentInput(quotation.customer_discount_per));
            }

            if (offsetPointsType === 1) {
              setOffsetPointsAmountInput(
                String(Number(quotation.offset_points_amount ?? 0))
              );
            } else if (offsetPointsType === 3) {
              setOffsetPoints(toPercentInput(quotation.offset_point));
              setOffsetPointsAmountInput(
                String(Number(quotation.offset_points_amount ?? 0))
              );
            } else {
              setOffsetPoints(toPercentInput(quotation.offset_point));
            }
          }

          setInsurerQuotation(quotation || null);
          setIsFetchingQuotation(false);
          setIsQuotationPending(false);
        })
        .catch((error: any) => {
          if (requestId !== insurerQuotationRequestId.current) return;
          setApiErrorMessage(
            error?.response?.data?.message ||
              "Unable to fetch insurer quotation."
          );
          setInsurerQuotation(null);
          setIsFetchingQuotation(false);
          setIsQuotationPending(false);
          console.error("Error fetching insurer quotation:", error);
        });
    };

    if (!hasRequestedInitialQuotation.current) {
      hasRequestedInitialQuotation.current = true;
      requestInsurerQuotation(true);
      return;
    }

    if (!hasUserEditedInputs.current) return;

    if (!areInputsValid) {
      setIsFetchingQuotation(false);
      setIsQuotationPending(false);
      return;
    }

    const debounceTimer = window.setTimeout(() => {
      requestInsurerQuotation();
    }, 500);

    return () => window.clearTimeout(debounceTimer);
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    requestCustomerDiscountPercent,
    requestCustomerDiscountAmount,
    customerDiscountType,
    requestOffsetPointsPercent,
    requestOffsetPointsAmount,
    show,
    leadId,
    isPopupInitialized,
  ]);

  const markEdited = () => {
    hasUserEditedInputs.current = true;
    insurerQuotationRequestId.current += 1;
    setIsQuotationPending(true);
  };

  const handleCustomerDiscountChange = (value: string) => {
    const normalizedValue = removeLeadingZero(value);
    if (/^\d{0,3}(\.\d*)?$/.test(normalizedValue)) {
      markEdited();
      setCustomerDiscountType(1);
      setCustomerDiscount(normalizedValue);
    }
  };

  const handleCustomerDiscountAmountChange = (value: string) => {
    const normalizedValue = String(HELPER.removeCurrencyFormattingId(value) || 0);
    markEdited();
    setCustomerDiscountType(2);
    setCustomerDiscountAmountInput(normalizedValue);
  };

  const handleOffsetPointsChange = (value: string) => {
    const normalizedValue = removeLeadingZero(value);
    if (/^\d{0,3}(\.\d*)?$/.test(normalizedValue)) {
      markEdited();
      setOffsetPointsType(1);
      setOffsetPoints(normalizedValue);
      // derived locally so the other cashback fields stay in step even when the typed value is invalid and no quotation is fetched
      setOffsetPointsAmountInput(String(gwpAmount > 0 ? Math.round((gwpAmount * Number(normalizedValue || 0)) / 100) : 0));
      setEffectiveOffsetPointsAmountInput("0");
    }
  };

  const handleOffsetPointsAmountChange = (value: string) => {
    const normalizedValue = String(HELPER.removeCurrencyFormattingId(value) || 0);
    markEdited();
    setOffsetPointsType(2);
    setOffsetPointsAmountInput(normalizedValue);
    setOffsetPoints(toPercentInput(gwpAmount > 0 ? (Number(normalizedValue) / gwpAmount) * 100 : 0));
    setEffectiveOffsetPointsAmountInput("0");
  };

  const handleEffectiveOffsetPointsAmountChange = (value: string) => {
    const normalizedValue = String(HELPER.removeCurrencyFormattingId(value) || 0);
    markEdited();
    setOffsetPointsType(3);
    setEffectiveOffsetPointsAmountInput(normalizedValue);
    const grossFromEffective =
      offsetPointsTaxPercent < 100 ? Math.round(Number(normalizedValue) / (1 - offsetPointsTaxPercent / 100)) : 0;
    setOffsetPoints(toPercentInput(gwpAmount > 0 ? (grossFromEffective / gwpAmount) * 100 : 0));
    setOffsetPointsAmountInput(String(grossFromEffective));
  };

  const isSubmitDisabled =
    !areInputsValid ||
    !insurerQuotation ||
    isFetchingQuotation ||
    isQuotationPending;

  const handleSubmit = () => {
    if (isSubmitDisabled) return;

    const appliedOffsetPointsValue = isOffsetPointsEditable
      ? offsetPointsValue
      : 0;

    handleChoosePaymentMethod({
      customerDiscountPercent: customerDiscountPercentExact,
      customerDiscountAmount:
        customerDiscountType === 2
          ? customerDiscountAmountValue
          : Number(insurerQuotation?.customer_discount ?? 0),
      customerDiscountType,
      offsetPointsPercent: offsetPointsType === 1 ? appliedOffsetPointsValue : 0,
      offsetPointsTaxPercent,
      netOffsetPointsPercent,
      effectiveOffsetPointsAmount: displayedEffectiveOffsetPointsAmount,
      agentCommissionPercent: netAgentCommissionPercent,
      pointCommissionPercent: displayedAgentPointsPercent,
      offsetPointsAmount: !isOffsetPointsEditable
        ? 0
        : offsetPointsType === 2
        ? offsetPointsAmountInputValue
        : offsetPointsType === 3
        ? computedGrossOffsetPointsAmountFromEffective
        : 0,
      offsetPointsType: offsetPointsType === 3 ? 2 : offsetPointsType,
      gwpAmount,
      agentCommissionAmount,
      agentPointAmount,
      premiumAmountPayable,
      netCommissionEarned,
    });
  };

  const cashbackCardClass = `additional-discount-card active ${
    isOffsetPointsEditable ? "" : "disabled"
  }`;

  return (
    <div className="additional-discount-popup-wrapper">
      <FullPageLoader show={isFetchingQuotation} />
      <Modal
        show={show && isPopupInitialized}
        handleClose={handleClose}
        hideCloseIcon
      >
        <div className="additional-discount-popup">
          <div className="additional-discount-header">
            <h2>Confirm Additional Discount</h2>
            <button
              type="button"
              className="additional-discount-close"
              aria-label="Close"
              onClick={handleClose}
            >
              <i className="ic-clearclose"></i>
            </button>
          </div>

          <div className="additional-discount-grid">
            {/* Row 1: Customer Discount (%) | Customer Discount (Amount) */}
            <div className="additional-discount-offset-group">
              <div className="additional-discount-card additional-discount-edit-card active">
                <span>Customer Discount (%)</span>
                <div className="additional-discount-input-wrap">
                  <input
                    type="text"
                    data-testid="customer-discount-percent"
                    value={customerDiscount}
                    onChange={(event) =>
                      handleCustomerDiscountChange(event.target.value)
                    }
                  />
                  <strong>%</strong>
                </div>
              </div>
            </div>

            <div className="additional-discount-offset-group">
              <div className="additional-discount-card additional-discount-edit-card active">
                <span>Customer Discount (Amount)</span>
                <div className="additional-discount-input-wrap">
                  <input
                    type="text"
                    data-testid="customer-discount-amount"
                    value={HELPER.addCurrencyFormatting(customerDiscountAmountInput)}
                    onChange={(event) =>
                      handleCustomerDiscountAmountChange(event.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Row 2: Customer Cashback (%) | Customer Cashback (Amount) */}
            <div className="additional-discount-offset-group">
              <div
                className={cashbackCardClass}
                title={
                  isOffsetPointsEditable
                    ? undefined
                    : `Available when Customer Discount is ${formatPercent(maxDiscountPercent)}`
                }
              >
                <span>Customer Cashback (%)</span>
                <div className="additional-discount-input-wrap">
                  <input
                    type="text"
                    data-testid="customer-cashback-percent"
                    value={offsetPoints}
                    onChange={(event) =>
                      handleOffsetPointsChange(event.target.value)
                    }
                    disabled={!isOffsetPointsEditable}
                  />
                  <strong>%</strong>
                </div>
              </div>

              <small className="additional-discount-helper">
                Maximum Usable Points:{" "}
                {formatPercent(maximumUsablePointsPercent)} (Effective Offset:{" "}
                {formatPercent(effectiveMaximumOffsetPercent)} after tax)
              </small>
            </div>

            <div className="additional-discount-offset-group">
              <div className={cashbackCardClass}>
                <span>Customer Cashback (Amount)</span>
                <div className="additional-discount-input-wrap">
                  <input
                    type="text"
                    data-testid="customer-cashback-amount"
                    value={HELPER.addCurrencyFormatting(offsetPointsAmountInput)}
                    onChange={(event) =>
                      handleOffsetPointsAmountChange(event.target.value)
                    }
                    disabled={!isOffsetPointsEditable}
                  />
                </div>
              </div>
            </div>

            {/* Row 3: Effective Customer Cashback (Amount) | Agent Commission (%) */}
            <div className="additional-discount-offset-group">
              <div className={cashbackCardClass}>
                <span>Effective Customer Cashback (Amount)</span>
                <div className="additional-discount-input-wrap">
                  <input
                    type="text"
                    data-testid="effective-customer-cashback-amount"
                    value={
                      offsetPointsType === 3
                        ? HELPER.addCurrencyFormatting(effectiveOffsetPointsAmountInput)
                        : HELPER.addCurrencyFormatting(displayedEffectiveOffsetPointsAmount)
                    }
                    onChange={(event) =>
                      handleEffectiveOffsetPointsAmountChange(event.target.value)
                    }
                    disabled={!isOffsetPointsEditable}
                  />
                </div>
              </div>
            </div>

            <div className="additional-discount-card">
              <span>Agent Commission (%)</span>
              <strong>{formatPercent(netAgentCommissionPercent)}</strong>
            </div>

            {/* Row 4: Agent Points (%) | Premium Amount Payable */}
            <div className="additional-discount-card">
              <span>Agent Points (%)</span>
              <strong>{formatPercent(displayedAgentPointsPercent)}</strong>
            </div>

            <div className="additional-discount-card">
              <span>Premium Amount Payable</span>
              <strong>{HELPER.addCurrencyFormatting(premiumAmountPayable)}</strong>
            </div>
          </div>

          {!isDiscountPrecisionValid && (
            <p className="additional-discount-error">
              Customer Discount (%): max 4 decimals allowed.
            </p>
          )}

          {!isOffsetPointsPrecisionValid && (
            <p className="additional-discount-error">
              Customer Cashback (%): max 4 decimals allowed.
            </p>
          )}

          {shouldShowValidationErrors && isDiscountPrecisionValid && !isValidDiscount && (
            <p className="additional-discount-error">
              Customer Discount (%) cannot be more than{" "}
              {formatPercent(customerDiscountTypingLimit)}.
            </p>
          )}

          {shouldShowValidationErrors && isOffsetPointsPrecisionValid && !isValidOffsetPoints && (
            <p className="additional-discount-error">
              Customer Cashback (%) cannot be more than the available Agent Points
              ({formatPercent(offsetPointsTypingLimit)}).
            </p>
          )}

          {shouldShowValidationErrors && !isValidCustomerDiscountAmount && (
            <p className="additional-discount-error">
              Customer Discount (Amount) cannot be more than{" "}
              {HELPER.addCurrencyFormatting(maxDiscountAmount)}.
            </p>
          )}

          {shouldShowValidationErrors && !isValidOffsetPointsAmount && (
            <p className="additional-discount-error">
              Customer Cashback (Amount) cannot be more than{" "}
              {HELPER.addCurrencyFormatting(offsetPointsTaxAmount)}.
            </p>
          )}

          {shouldShowValidationErrors && !isValidEffectiveOffsetPointsAmount && (
            <p className="additional-discount-error">
              Effective Customer Cashback (Amount) cannot be more than{" "}
              {HELPER.addCurrencyFormatting(maxEffectiveOffsetPointsAmount)}.
            </p>
          )}

          {apiErrorMessage && (
            <p className="additional-discount-error">{apiErrorMessage}</p>
          )}

          <div className="additional-discount-summary">
            Earned Net Commission{" "}
            <strong>{HELPER.addCurrencyFormatting(netCommissionEarned)}</strong>{" "}
            and Net Points{" "}
            <strong>{HELPER.addPointsFormatting(netPointsEarned)}</strong>
          </div>

          <button
            type="button"
            className="btn-primary additional-discount-action"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            Choose Payment Method
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdditionalDiscountPopup;
